<!-- eslint-disable prettier/prettier -->
<template>
  <v-dialog
    :model-value="modelValue"
    :max-width="smAndDown ? undefined : 760"
    :fullscreen="smAndDown"
    :transition="smAndDown ? 'dialog-bottom-transition' : 'dialog-transition'"
    :content-class="
      smAndDown
        ? 'create-account-dialog create-account-dialog--mobile'
        : 'create-account-dialog'
    "
    @update:model-value="onDialogToggle"
  >
    <div class="create-account">
      <button class="close-btn" type="button" @click="closeDialog">
        <v-icon icon="mdi-close" />
      </button>

      <img
        class="brand-logo"
        src="/img/logo-f-123x190.webp"
        alt="FlixLatino"
      />

      <h2 class="title">{{ $t("avodDialogs.createAccount.title") }}</h2>
      <p class="subtitle">
        {{ $t("avodDialogs.createAccount.subtitle") }}
      </p>


      <form class="form" @submit.prevent="onSubmit">
        <div class="field-shell">
          <input
            v-model.trim="form.email"
            class="field"
            type="email"
            :placeholder="$t('avodDialogs.createAccount.emailPlaceholder')"
            autocomplete="email"
            required
          />

          <button class="cta-btn" type="submit" :disabled="submitting">
            {{ submitting ? $t("avodDialogs.createAccount.submitting") : $t("avodDialogs.createAccount.submit") }}
          </button>
        </div>
      </form>

      <button class="login-link" type="button" @click="$emit('login')">
        {{ $t("avodDialogs.createAccount.login") }}
      </button>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { reactive, ref } from "vue";
import { useDisplay } from "vuetify";
import { FlixlatinoApiClient } from "../../../services/FlixlatinoApiClient";

const props = withDefaults(
  defineProps<{
    modelValue?: boolean;
  }>(),
  {
    modelValue: false,
  }
);

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "close"): void;
  (
    e: "success",
    payload: {
      email: string;
      response: any;
    }
  ): void;
  (e: "login"): void;
}>();

const { smAndDown } = useDisplay();
const apiClient = new FlixlatinoApiClient();

const form = reactive({
  email: "",
});
const submitting = ref(false);

function onDialogToggle(value: boolean) {
  emit("update:modelValue", value);
  if (!value) emit("close");
}

function closeDialog() {
  emit("update:modelValue", false);
  emit("close");
}

async function onSubmit() {
  const email = String(form.email || "").trim();
  if (!email || submitting.value) {
    return;
  }

  submitting.value = true;
  try {
    const response = await apiClient.createUnattended(email);
    const responseData = response as any;
    console.debug("magic-link response", responseData);
    if (Number(responseData?.code) !== 200) {
      return;
    }

    emit("success", {
      email,
      response: responseData,
    });
  } finally {
    submitting.value = false;
  }
}
</script>

<style scoped>
.create-account {
  position: relative;
  width: 760px;
  min-height: 600px;
  border-radius: 48px;
  border: 1px solid transparent;
  background: radial-gradient(
      75% 80% at 30% 20%,
      rgba(57, 66, 118, 0.6) 0%,
      rgba(31, 39, 81, 0.38) 38%,
      rgba(16, 24, 57, 0.9) 100%
    ),
    linear-gradient(180deg, #1c2650 0%, #11183b 100%);
  padding: 64px 56px 52px;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  overflow: hidden;
  box-sizing: border-box;
}

.create-account::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 3rem;
  padding: 2px;
  background: linear-gradient(
    135deg,
    rgba(158, 120, 255, 0.95) 0%,
    rgba(119, 86, 255, 0.9) 35%,
    rgba(255, 64, 153, 0.9) 70%,
    rgba(255, 64, 153, 0.95) 100%
  );
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  mask-composite: exclude;
  pointer-events: none;
}

.close-btn {
  position: absolute;
  top: 24px;
  right: 24px;
  width: 36px;
  height: 36px;
  background: transparent;
  border: none;
  color: #f2f5ff;
}

.brand-logo {
  width: 82px;
  height: auto;
  margin-bottom: 28px;
}

.title {
  margin: 0 0 10px 0;
  font-size: 2rem;
  line-height: 120%;
  font-weight: 800;
  letter-spacing: 0;
}

.subtitle {
  margin: 0;
  max-width: 420px;
  color: #bac7f3;
  font-size: 1.18rem;
  line-height: 1.5;
}

.form {
  width: 100%;
  max-width: 560px;
  margin-top: 30px;
}

.field-shell {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px;
  border-radius: 18px;
  border: 1px solid rgba(188, 196, 221, 0.54);
  background: rgba(17, 24, 59, 0.45);
  box-sizing: border-box;
}

.field {
  flex: 1 1 auto;
  width: 100%;
  height: 56px;
  border-radius: 14px;
  border: none;
  background: transparent;
  color: #f4f7ff;
  font-size: 1rem;
  padding: 0 18px;
  outline: none;
  text-align: start;
}

.field::placeholder {
  color: rgba(214, 220, 241, 0.92);
  text-align: start;
}

.field-shell:focus-within {
  border-color: rgba(243, 246, 255, 0.85);
}

.cta-btn {
  height: 56px;
  border: none;
  border-radius: 14px;
  background: #fa002b;
  color: #fff;
  font-size: 1rem;
  font-weight: 900;
  width: auto;
  min-width: 190px;
  padding: 0 24px;
  flex: 0 0 auto;
}

.login-link {
  margin-top: 30px;
  background: transparent;
  border: none;
  color: #f7f8fd;
  font-size: 1rem;
}

@media (max-width: 959px) {
  :deep(.create-account-dialog--mobile) {
    margin: 0;
    width: 100vw !important;
    max-width: 100vw !important;
    max-height: 100vh !important;
    height: 100vh !important;
  }

  .create-account {
    border-radius: 0;
    min-height: 100vh;
    width: 100%;
    padding: 42px 20px 30px;
  }

  .create-account::before {
    border-radius: 0;
  }

  .close-btn {
    top: 12px;
    right: 12px;
  }

  .brand-logo {
    width: 48px;
    margin-bottom: 18px;
  }

  .title {
    font-size: 30px;
    margin-bottom: 8px;
  }

  .subtitle {
    font-size: 18px;
    line-height: 1.45;
  }

  .form {
    margin-top: 24px;
    max-width: 440px;
  }

  .field-shell {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    padding: 0;
    border: none;
    background: transparent;
  }

  .field {
    height: 54px;
    border-radius: 18px;
    font-size: 14px;
    padding: 0 18px;
    border: 1px solid rgba(188, 196, 221, 0.54);
    background: rgba(17, 24, 59, 0.45);
  }

  .cta-btn {
    width: 100%;
    min-width: 0;
    height: 56px;
    border-radius: 1rem;
    font-size: 1rem;
    margin-top: 0;
  }

  .login-link {
    margin-top: 24px;
    font-size: 16px;
  }
}
</style>
